import type { AdminUsersListParams, AdminUsersQueryInput } from '../-types';

/**
 * Turns the table's state into the endpoint's parameters.
 *
 * This is the single conversion point on purpose: the query key is the object it
 * returns, so the route loader and the component's hook only share a cache entry
 * if both derive their params the same way. The loader reads them off the URL
 * and `useTable` reads them off its own state, which are the same values in two
 * different shapes — normalising here is what makes the prefetch land.
 *
 * Two translations happen:
 *
 *  - **Page base.** `useTable` counts pages from 1; `AdminUsersListQuerySchema`
 *    takes `page` from 0 (`.min(0).default(0)`). Sending the UI's number
 *    straight through silently skips the first page.
 *  - **Filters.** The endpoint takes one JSON object of `column: value` and
 *    `LIKE`s each value case-insensitively. Values are joined the way
 *    `useTable` joins them, but note that only a single value per column
 *    actually works: `LIKE '%a,b%'` matches nothing. Every filter this table
 *    offers is single-valued for that reason.
 *
 * `withDelegatedUsers` is deliberately absent: the route parses it out of the
 * query and never forwards it to the service (`backend/src/api/routes/admin/
 * users/get.list.ts`), so the list is always the delegated one. The old
 * dashboard passes `true` and gets exactly what it would without it.
 */
export function toAdminUsersParams(
  input: AdminUsersQueryInput,
): AdminUsersListParams {
  const filter: Record<string, string> = {};
  for (const [key, values] of Object.entries(input.filters ?? {})) {
    if (values && values.length > 0) filter[key] = values.join(',');
  }
  const hasFilter = Object.keys(filter).length > 0;

  return {
    page: Math.max(0, input.page - 1),
    count: input.limit,
    ...(input.sort ? { sort: input.sort } : {}),
    ...(hasFilter ? { filter: JSON.stringify(filter) } : {}),
  };
}

/** Serialises already-converted params. */
export function buildAdminUsersQs(params: AdminUsersListParams): string {
  const qs = new URLSearchParams();
  qs.set('page', String(params.page));
  qs.set('count', String(params.count));
  if (params.sort) qs.set('sort', params.sort);
  if (params.filter) qs.set('filter', params.filter);
  return qs.toString();
}
